/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './DiffChecker';

const DiffCheckerManager: ComponentManagerFn<'DiffChecker'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'DiffChecker'>(platform, 'DiffChecker'),
    type: 'DiffChecker',
    name: 'Diff Checker',
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'string',
      },
      originalValue: {
        label: 'Original Value Attribute',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      modifiedValue: {
        label: 'Modified Value Attribute',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      language: {
        label: 'Language',
        type: 'string',
        helpText:
          'A supported language by the Monaco Editor e.g. typescript, xml, java, plsql etc.',
      },
    },
    getDisplayText: (item) => '',
    category: 'Forms',
    icon: { iconName: 'line-columns' },
    description: `**Overview**<br/>
The Diff Checker component is a specialized visual tool used to compare two text values side-by-side or inline. It highlights the differences between an "Original" version and a "Modified" version, making it easy to spot additions, deletions, and changes.
Use this component when building audit trails, version control interfaces, or approval workflows where users need to understand exactly what has changed between two states of data.

**1. Comparison Data**<br/>
To function, the component requires two distinct inputs. Both inputs accept expressions, allowing you to dynamically pull data from different stores or variables.
* **Original Value Attribute:** The baseline or "before" text. (Example: \`$.auditLog.previousState\`).
* **Modified Value Attribute:** The new or "after" text to compare against. (Example: \`$.auditLog.currentState\`).

**2. Visualization & Syntax**<br/>
The Diff Checker utilizes the Monaco Editor engine (similar to the Code component) to provide intelligent syntax highlighting, making complex data easier to read.
* **Language:** Specifies the format of the data being compared. This ensures that keywords, brackets, and strings are colored correctly.
* **Common Values:** \`json\`, \`xml\`, \`typescript\`, \`sql\`, \`html\`.
* **Impact:** If you are comparing two JSON objects, setting this to **json** allows the differ to respect formatting and folding, making the diff significantly more readable than plain text.
* **Title:** An optional header displayed above the comparison view to provide context.

**3. Best Use Cases**<br/>
* **Audit Logs:** Displaying the specific field changes in a "History" view (e.g., showing how a user modified a configuration JSON).
* **Code Review:** Comparing an old SQL query or Script against a proposed update.
* **Content Management:** Comparing revisions of a blog post or HTML email template.
* **Data Reconciliation:** Visually identifying discrepancies between two large text blobs or imported files.`,
  };
};

export default DiffCheckerManager;
